import { Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow } from "@mui/material";
import * as React from "react";
import {
  BooleanInput,
  ReferenceInput,
  SelectInput,
  TextInput,
  required,
  useRecordContext,
  SelectArrayInput,
  AutocompleteInput,
  NumberInput,
  PasswordInput,
  Create
} from "react-admin";
import { CustomerRoleEnum, AdminRoleEnum } from "../../common/enum/RoleEnum";
import { StatusEnum } from "src/common/enum";
import { PhoneInput } from "src/components/input/PhoneInput";
import { FormTab, TabbedForm } from "src/enterprise/ra-rbac";
import Transformer from "../../common/Transformer";
import { formatDateTime } from "src/common/UtilFunctions";
import { UserCreateComponent } from "./UserComponent";
import { UserHistoryTypeEnumIcons } from "src/common/enum";
import { InputSeparator } from '../../components/input';

export const UserCreate = () => {
  const filterToQuery = (searchText) => ({
    "search": searchText,
  });

  const equalToPassword = (value, allValues) => {
    if (value !== allValues.password) {
        return "Passwords must match";
    }
  }

  const transform = (data) => {
    Transformer(data, {
      remove: ["customerRoles", "adminRoles", "confirmPassword"],
      update: {
        group: data.group ? data.group.id : null,
        territory: data.territory ? data.territory.id : null,
        roles: ["ROLE_USER", ...data.customer_roles || [], ...data.admin_roles || []],
        sampleOrderForm: data.sample_order_form !== null ? data.sample_order_form.toString() : "false",
        verified: true,
        contract: data.contract !== null ? data.contract.toString() : "false",
        confirmed: true
      },
    });

    return data;
  };

  return (
    <Create component={UserCreateComponent}  transform={transform}>
      <TabbedForm>
        <FormTab label="General" name="general">
          <Grid container rowSpacing={1} columnSpacing={3}>
            <Grid container item sm={6} rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12} md={12}>
                <TextInput source="email" type="email" validate={required()} />
              </Grid>

              <Grid item sm={12} md={12}>
                <PasswordInput source="password" validate={required()} />
              </Grid>

              <Grid item sm={12} md={12}>
                <PasswordInput source="confirm_password" validate={equalToPassword}/>
              </Grid>
            </Grid>
            <Grid container item sm={12} md={12}>
                <InputSeparator title="" />
            </Grid>
            <Grid container item sm={12} rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12} md={6}>
                <TextInput source="legacy_id" label="Legacy ID"/>
              </Grid> 
            </Grid>
            <Grid item sm={12} md={6} lg={3}>
              <TextInput source="first_name" validate={required()} />
            </Grid>
            <Grid item sm={12} md={6} lg={3}>
              <TextInput source="last_name" validate={required()} />
            </Grid>

            <Grid item sm={12} lg={6}>
              <TextInput source="website" />
            </Grid>

            <Grid item sm={12} md={6}>
              <PhoneInput sx={{ width: "100%" }} source="phone" label="Phone" />
            </Grid>

            <Grid item sm={12} md={6}>
              <PhoneInput
                sx={{ width: "100%" }}
                source="mobile"
                label="Mobile"
                required={false}
              />
            </Grid>

            <Grid item sm={12} md={6} lg={3}>
              <SelectInput source="status" choices={StatusEnum} validate={required()} />
            </Grid>
    
            <Grid item sm={12} lg={3}>
              <TextInput source="company" />
            </Grid>

            
          </Grid>
        </FormTab>
        <FormTab label="Administrative" name="administrativeCreate">
          <Grid container rowSpacing={1} columnSpacing={2}>
            <Grid item sm={6} md={6} lg={6}>
              <BooleanInput source="contract"></BooleanInput>
            </Grid>
            <Grid item sm={6} md={6} lg={6}>
              <BooleanInput source="sample_order_form" label="Show Sample Order Form"></BooleanInput>
            </Grid>
            <Grid item sm={12} md={6}>
              <ReferenceInput source="group.id" reference="admin/groups">
                <AutocompleteInput
                  filterToQuery={(e) => filterToQuery(e)}
                  optionText={(record) => record.name + " - " + record.email}
                  size="small"
                  label="Group"
                  defaultValue=""
                />
              </ReferenceInput>

              <ReferenceInput source="territory.id" reference="admin/territories">
                <AutocompleteInput
                  filterToQuery={(e) => filterToQuery(e)}
                  optionText={(record) => record.name + " - " + record.email}
                  size="small"
                  label="Territory"
                  defaultValue=""
                />
              </ReferenceInput>
            </Grid>
            <Grid item sm={12} md={6}>
              <TextInput
                source="notes"
                multiline
                resettable
                sx={{
                  "#notes": { minHeight: "87.5px", height: "100%" },
                }}
              />
            </Grid>
            <Grid item sm={12} md={6}>
              <Grid container rowSpacing={1} columnSpacing={2}>
                <Grid item md={6}>
                  <SelectArrayInput source="customer_roles" label="Customer Roles" choices={CustomerRoleEnum} />
                </Grid>
                <Grid item md={6}>
                  <SelectArrayInput source="admin_roles" label="Admin Roles" choices={AdminRoleEnum} />
                </Grid>
              </Grid>
            </Grid>
            <Grid item sm={12} md={3}>
              <NumberInput source="credit" min={0} defaultValue={0}/>
            </Grid> 
          </Grid>
        </FormTab>
      </TabbedForm>
    </Create>
  );
};


const HistoriesTable = () => {
  const record = useRecordContext();
  const rows = record ? (record.histories ? record.histories : []) : [];

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper sx={{width: "100%"}}>
      <TableContainer sx={{border: "1px solid #ebebeb", borderRadius: "8px", boxShadow: "0px 1px 1px rgba(100, 116, 139, 0.06), 0px 1px 2px rgba(100, 116, 139, 0.1)"}}>
        <Table sx={{ minWidth: 650, width: "100%" }} size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                Type
              </TableCell>
              <TableCell>
                Message
              </TableCell>
              <TableCell>
                Created At
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...rows].slice().reverse().slice(page * rowsPerPage, (page+1) * rowsPerPage).map((row) => {
              let typeEnum = UserHistoryTypeEnumIcons.find((type) => type.id === row.type);

              return (
                <TableRow key={row.id}>
                  <TableCell sx={{width: "0px",whiteSpace: "nowrap"}}>
                    {typeEnum && typeEnum.icon}
                  </TableCell>
                  <TableCell  sx={{width: "100%"}}>
                    {row.message}
                  </TableCell>
                  <TableCell sx={{width: "0px",whiteSpace: "nowrap"}}>
                    {formatDateTime(row.created_at)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 20, 50]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};
